import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext(null);

// Translation data
const translations = {
    en: {
        // Navigation
        home: 'Home',
        dashboard: 'Calendar',
        login: 'Login',
        register: 'Register',
        logout: 'Logout',
        profile: 'Profile',
        settings: 'Settings',
        
        // Landing
        heroTitle: 'Professional Online Tong Shu Plotter',
        heroSubtitle: 'Practitioner-grade Chinese almanac calculations for date selection and consultation',
        getStarted: 'Get Started',
        learnMore: 'Learn More',
        
        // Features
        features: 'Features',
        accurateCalculations: 'Accurate Calculations',
        accurateDesc: 'Based on the Chinese Solar Calendar (节气) and traditional classical rules',
        comprehensiveData: 'Comprehensive Data',
        comprehensiveDesc: '12 Day Officers, 28 Constellations, Na Yin, and more',
        educationalMode: 'Educational Mode',
        educationalDesc: 'Toggle between Beginner and Practitioner modes',
        
        // Dashboard
        dailyOverview: 'Daily Overview',
        selectDate: 'Select Date',
        today: 'Today',
        monthView: 'Month View',
        dayOfficer: 'Day Officer',
        lunarMansion: 'Lunar Mansion',
        naYin: 'Na Yin',
        dongGong: 'Dong Gong Rating',
        yellowPath: 'Yellow Path',
        blackPath: 'Black Path',
        pillars: 'Pillars',
        yearPillar: 'Year',
        monthPillar: 'Month',
        dayPillar: 'Day',
        directions: 'Directions',
        clashWarnings: 'Clash Warnings',
        activities: 'Activities',
        recommended: 'Recommended',
        avoid: 'Avoid',
        
        // Auth
        email: 'Email',
        password: 'Password',
        name: 'Name',
        confirmPassword: 'Confirm Password',
        forgotPassword: 'Forgot Password?',
        noAccount: "Don't have an account?",
        haveAccount: 'Already have an account?',
        signUp: 'Sign Up',
        signIn: 'Sign In',
        
        // Settings
        timezone: 'Timezone',
        language: 'Language',
        mode: 'Mode',
        beginner: 'Beginner',
        practitioner: 'Practitioner',
        save: 'Save',
        cancel: 'Cancel',
        
        // Ratings
        auspicious: 'Auspicious',
        inauspicious: 'Inauspicious',
        neutral: 'Neutral',
        
        // Directions
        happiness: 'Happiness',
        wealth: 'Wealth',
        conflict: 'Conflict',
        fiveGhost: 'Five Ghost',
        
        // Elements
        water: 'Water',
        wood: 'Wood',
        fire: 'Fire',
        earth: 'Earth',
        metal: 'Metal',
    },
    zh: {
        // Navigation
        home: '首页',
        dashboard: '日历',
        login: '登录',
        register: '注册',
        logout: '退出',
        profile: '个人资料',
        settings: '设置',
        
        // Landing
        heroTitle: '专业通书排盘系统',
        heroSubtitle: '基于节气的专业级中国日历计算，用于择日和咨询',
        getStarted: '开始使用',
        learnMore: '了解更多',
        
        // Features
        features: '功能特点',
        accurateCalculations: '精准计算',
        accurateDesc: '基于节气和传统经典规则的计算',
        comprehensiveData: '全面数据',
        comprehensiveDesc: '建除十二神、二十八宿、纳音等',
        educationalMode: '教学模式',
        educationalDesc: '可在初学者和专业模式间切换',
        
        // Dashboard
        dailyOverview: '每日概览',
        selectDate: '选择日期',
        today: '今天',
        monthView: '月历视图',
        dayOfficer: '建除十二神',
        lunarMansion: '二十八宿',
        naYin: '纳音',
        dongGong: '董公择日',
        yellowPath: '黄道',
        blackPath: '黑道',
        pillars: '四柱',
        yearPillar: '年柱',
        monthPillar: '月柱',
        dayPillar: '日柱',
        directions: '方位',
        clashWarnings: '冲煞提醒',
        activities: '宜忌',
        recommended: '宜',
        avoid: '忌',
        
        // Auth
        email: '电子邮箱',
        password: '密码',
        name: '姓名',
        confirmPassword: '确认密码',
        forgotPassword: '忘记密码？',
        noAccount: '还没有账号？',
        haveAccount: '已有账号？',
        signUp: '注册',
        signIn: '登录',
        
        // Settings
        timezone: '时区',
        language: '语言',
        mode: '模式',
        beginner: '初学者',
        practitioner: '专业',
        save: '保存',
        cancel: '取消',
        
        // Ratings
        auspicious: '吉',
        inauspicious: '凶',
        neutral: '平',
        
        // Directions
        happiness: '喜神',
        wealth: '财神',
        conflict: '冲煞',
        fiveGhost: '五鬼',
        
        // Elements
        water: '水',
        wood: '木',
        fire: '火',
        earth: '土',
        metal: '金',
    }
};

export const useLanguage = () => {
    const context = useContext(LanguageContext);
    if (!context) {
        throw new Error('useLanguage must be used within LanguageProvider');
    }
    return context;
};

export const LanguageProvider = ({ children }) => {
    const [language, setLanguage] = useState(() => {
        return localStorage.getItem('language') || 'en';
    });

    useEffect(() => {
        localStorage.setItem('language', language);
    }, [language]);

    const t = (key) => {
        return translations[language]?.[key] || translations.en[key] || key;
    };

    const toggleLanguage = () => {
        setLanguage(prev => prev === 'en' ? 'zh' : 'en');
    };

    const value = {
        language,
        setLanguage,
        toggleLanguage,
        t,
        isEnglish: language === 'en',
        isChinese: language === 'zh'
    };

    return (
        <LanguageContext.Provider value={value}>
            {children}
        </LanguageContext.Provider>
    );
};

export default LanguageContext;
